import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Activity, Calendar, FileText, MessageSquare, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_app/")({
  component: Dashboard,
});

interface Overview {
  pages: { n: number; pub: number };
  upcoming_events: number;
  published_news: number;
  live_popups: number;
  chat_sessions_7d: number;
  payments_7d: { count: number; total_inr: number };
}

function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", "overview"],
    queryFn: () => api.get<Overview>("/admin/analytics/overview"),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-light tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">A quick read on what's happening across KRCE.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat icon={FileText}      label="Pages"            value={isLoading ? "…" : `${data?.pages.pub ?? 0} / ${data?.pages.n ?? 0}`} sub="published / total" />
        <Stat icon={Calendar}      label="Upcoming Events"  value={isLoading ? "…" : String(data?.upcoming_events ?? 0)} />
        <Stat icon={Activity}      label="News Published"   value={isLoading ? "…" : String(data?.published_news ?? 0)} />
        <Stat icon={MessageSquare} label="Chat 7-day"       value={isLoading ? "…" : String(data?.chat_sessions_7d ?? 0)} sub="sessions" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payments · 7 days</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-light">{isLoading ? "…" : `₹${((data?.payments_7d.total_inr ?? 0) / 100).toLocaleString("en-IN")}`}</p>
            <p className="text-xs text-muted-foreground mt-1">{data?.payments_7d.count ?? 0} transactions captured</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Live Popups</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-light">{isLoading ? "…" : data?.live_popups}</p>
            <p className="text-xs text-muted-foreground mt-1">currently visible to visitors</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick links</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <a href="/events" className="rounded-md border p-3 hover:bg-secondary">📅 Events</a>
          <a href="/content/news" className="rounded-md border p-3 hover:bg-secondary">📰 News</a>
          <a href="/content/announcements" className="rounded-md border p-3 hover:bg-secondary">🔔 Announcements</a>
          <a href="/payments" className="rounded-md border p-3 hover:bg-secondary">💳 Payments</a>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="mt-1 text-2xl font-light">{value}</p>
            {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
          </div>
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}
